import { Avatar, AvatarFallback } from "@cmp-src/primitives/avatar";
import { IUserFull } from "@gettactic/api";
import { cn, getInitials } from "@gettactic/helpers";
import {
  getAvatarGradient,
  getAvatarTextColor,
  getRandomColor
} from "@gettactic/helpers/src/colors";
import { cva, VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import Image from "next/image";
import React from "react";

export const avatarVariants = cva(
  "rounded-full aspect-square shrink-0 font-inter font-semibold",
  {
    variants: {
      size: {
        xs: "h-5 w-5 text-[10px]",
        s: "h-6 w-6 text-[9.5px] leading-[9.5px]",
        sm: "h-8 w-8",
        sm2: "h-9 w-9",
        md: "h-10 w-10",
        lg: "h-14 w-14",
        xl: "h-[70px] w-[70px]",
        "2xl": "h-24 w-24 sm:h-32 sm:w-32",
        full: "h-full w-full"
      }
    },
    defaultVariants: {
      size: "xl"
    }
  }
);

export interface UserAvatarProps extends VariantProps<typeof avatarVariants> {
  user: Partial<IUserFull>;
  className?: string;
  isPlaneImage?: boolean;
  onClose?: () => void; // ← NEW
}

export const UserAvatar: React.FC<UserAvatarProps> = React.memo(
  ({ user, size, className, isPlaneImage, onClose }) => {
    const initials = getInitials(user.name || "");
    let bgColor = initials
      ? getRandomColor({ string: user.name || "" })
      : undefined;

    const isDeleted = user.roles?.includes("deleted");
    if (isDeleted) {
      bgColor = "gray";
    }

    const imageRender = isPlaneImage ? (
      <img
        alt={user.name || ""}
        src={user.profile_photo_url ?? ""}
        className={cn(
          "absolute inset-0 h-full w-full object-cover",
          isDeleted ? "grayscale" : ""
        )}
      />
    ) : (
      <Image
        layout="fill"
        src={user.profile_photo_url ?? ""}
        className={cn("object-cover", isDeleted ? "grayscale" : "")}
        alt={""}
      />
    );

    return (
      <div className="relative">
        <Avatar
          className={cn(
            avatarVariants({ size }),
            { grayscale: isDeleted },
            className
          )}
        >
          {!!user.profile_photo_url && imageRender}
          <AvatarFallback
            style={
              initials && bgColor
                ? {
                    backgroundImage: getAvatarGradient(bgColor),
                    color: getAvatarTextColor(bgColor)
                  }
                : { backgroundColor: "#9CA3AF" }
            }
          >
            {initials}
          </AvatarFallback>
        </Avatar>
        {onClose && (
          <button
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();

              onClose();
            }}
            type={"button"}
            className={
              "-right-1 -top-1 absolute flex h-4 w-4 items-center justify-center rounded-full bg-black/70 text-white transition-colors hover:bg-black"
            }
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.size === nextProps.size &&
      prevProps.user?.profile_photo_url === nextProps.user?.profile_photo_url &&
      prevProps.user?.name === nextProps.user?.name &&
      prevProps.user?.id === nextProps.user?.id &&
      prevProps.onClose === nextProps.onClose
    );
  }
);
